import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore, useRoomStore } from '../stores';
import {
  SpadeSvg,
  HeartSvg,
  DiamondSvg,
  ClubSvg,
  BackArrowSvg,
  LogoutSvg,
} from '../components/SuitIcons';

type AIDifficulty = 'easy' | 'medium' | 'hard';

const AI_OPTIONS = [
  {
    difficulty: 'easy' as const,
    label: 'Easy',
    description: 'Random play — good for learning',
    detail: 'AI plays random valid cards. Great for understanding the rules.',
  },
  {
    difficulty: 'medium' as const,
    label: 'Medium',
    description: 'Strategic calling and card tracking',
    detail: 'AI considers trump, lead suit, and basic card counting.',
  },
  {
    difficulty: 'hard' as const,
    label: 'Hard',
    description: 'Probability-based with full card tracking',
    detail: 'AI tracks all played cards and calculates optimal moves.',
  },
];

export function SinglePlayerSetupPage() {
  const [selectedDifficulty, setSelectedDifficulty] = useState<AIDifficulty>('medium');
  const [isStarting, setIsStarting] = useState(false);
  const roomStarting = useRoomStore((s) => s.roomStarting);
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();

  function handleStart() {
    setIsStarting(true);
    roomStarting();
    const roomCode = `sp_${Date.now()}`;
    navigate(`/game/${roomCode}?mode=single&ai=${selectedDifficulty}`);
  }

  return (
    <div className="page setup-page">
      {/* Floating SVG background suits — same as HomePage */}
      <div className="page-bg-suits" aria-hidden="true">
        <SpadeSvg className="page-bg-icon page-bg-1" />
        <HeartSvg className="page-bg-icon page-bg-2" />
        <DiamondSvg className="page-bg-icon page-bg-3" />
        <ClubSvg className="page-bg-icon page-bg-4" />
        <SpadeSvg className="page-bg-icon page-bg-5" />
        <HeartSvg className="page-bg-icon page-bg-6" />
      </div>

      {/* Top bar — same pattern as HomePage */}
      <div className="page-topbar">
        <button className="page-back" onClick={() => navigate('/')}>
          <BackArrowSvg />
          Back
        </button>
        <div className="page-topbar-right">
          {user && <span className="page-username">♠ {user.username}</span>}
          <button className="page-logout" onClick={logout}>
            <LogoutSvg />
            Logout
          </button>
        </div>
      </div>

      {/* Hero */}
      <div className="setup-hero">
        <div className="setup-hero-icon">
          <SpadeSvg className="hero-suit-svg" />
        </div>
        <h1>Single Player</h1>
        <p className="setup-subtitle">Challenge 3 AI opponents in Call Bridge</p>
      </div>

      <div className="setup-section-label">Select Difficulty</div>

      <div className="difficulty-grid">
        {AI_OPTIONS.map((opt, idx) => (
          <button
            key={opt.difficulty}
            className={`difficulty-card ${selectedDifficulty === opt.difficulty ? 'selected' : ''}`}
            onClick={() => setSelectedDifficulty(opt.difficulty)}
          >
            <span className="diff-rank">{['♠', '♦', '♛'][idx]}</span>
            <div className="diff-info">
              <h3>{opt.label}</h3>
              <p>{opt.description}</p>
              {selectedDifficulty === opt.difficulty && <p className="diff-detail">{opt.detail}</p>}
            </div>
          </button>
        ))}
      </div>

      <div className="setup-match-info">
        <div className="match-info-item">
          <span className="match-info-label">Players</span>
          <span className="match-info-value">1 vs 3 AI</span>
        </div>
        <div className="match-info-divider" />
        <div className="match-info-item">
          <span className="match-info-label">Rounds</span>
          <span className="match-info-value">5</span>
        </div>
        <div className="match-info-divider" />
        <div className="match-info-item">
          <span className="match-info-label">Trump</span>
          <span className="match-info-value">♠ Spades</span>
        </div>
      </div>
      <p className="setup-open-round-note">
        Round 1 is an open round — no calling, all tricks count toward your score.
      </p>

      <button
        className="btn btn-primary btn-lg setup-start-btn"
        onClick={handleStart}
        disabled={isStarting}
      >
        {isStarting ? (
          <span className="start-loading">
            <span className="start-spinner" /> Starting...
          </span>
        ) : (
          <>
            <span className="start-icon">▶</span> Start Game
          </>
        )}
      </button>
    </div>
  );
}
